import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Send, Phone, Mail, MapPin, CheckCircle } from 'lucide-react';
import api, { formatApiError } from '../lib/api';
import SEO from '../components/SEO';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/contact', form);
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Hiba történt. Kérjük próbálja újra.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Kapcsolat"
        description="Vegye fel velünk a kapcsolatot! Írjon nekünk és 24 órán belül testre szabott ajánlatot küldünk."
      />
      <section className="py-20 sm:py-28 bg-[#0B1B3D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-[#C5A059] text-xs uppercase tracking-[0.2em] font-semibold">Kapcsolat</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-['Outfit'] font-light text-white tracking-tight">
            Vegye fel velünk a <span className="font-semibold">kapcsolatot</span>
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-2xl">
            Írjon nekünk és 24 órán belül felvesszük Önnel a kapcsolatot testre szabott ajánlattal.
          </p>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg border border-[#E2E8F0] p-8 sm:p-10">
                {success ? (
                  <div data-testid="contact-success" className="text-center py-12">
                    <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-['Outfit'] font-semibold text-[#0B1B3D]">Üzenet elküldve!</h3>
                    <p className="mt-3 text-[#64748B]">Köszönjük megkeresését. Hamarosan felvesszük Önnel a kapcsolatot.</p>
                    <Button
                      data-testid="contact-reset-btn"
                      onClick={() => setSuccess(false)}
                      variant="outline"
                      className="mt-6 border-[#0B1B3D] text-[#0B1B3D]"
                    >
                      Új üzenet küldése
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <h2 className="text-2xl font-['Outfit'] font-semibold text-[#0B1B3D] mb-2">Üzenetküldés</h2>
                    {error && (
                      <div data-testid="contact-error" className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                        {error}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name" className="text-[#1A1F2C] font-medium">Név *</Label>
                        <Input
                          id="name"
                          data-testid="contact-name-input"
                          value={form.name}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          required
                          placeholder="Teljes név"
                          className="mt-1.5 border-[#E2E8F0] focus:border-[#0B1B3D] focus:ring-[#0B1B3D]"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-[#1A1F2C] font-medium">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="contact-email-input"
                          value={form.email}
                          onChange={e => setForm({ ...form, email: e.target.value })}
                          required
                          placeholder="pelda@email.hu"
                          className="mt-1.5 border-[#E2E8F0] focus:border-[#0B1B3D] focus:ring-[#0B1B3D]"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-[#1A1F2C] font-medium">Telefonszám</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={e => setForm({ ...form, phone: e.target.value })}
                        placeholder="+36 30 123 4567"
                        className="mt-1.5 border-[#E2E8F0] focus:border-[#0B1B3D] focus:ring-[#0B1B3D]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-[#1A1F2C] font-medium">Üzenet *</Label>
                      <Textarea
                        id="message"
                        data-testid="contact-message-input"
                        value={form.message}
                        onChange={e => setForm({ ...form, message: e.target.value })}
                        required
                        rows={5}
                        placeholder="Írja le, miben segíthetünk..."
                        className="mt-1.5 border-[#E2E8F0] focus:border-[#0B1B3D] focus:ring-[#0B1B3D]"
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={loading}
                      size="lg"
                      className="w-full bg-[#0B1B3D] hover:bg-[#162B56] text-white font-semibold"
                    >
                      {loading ? 'Küldés...' : 'Üzenet küldése'} <Send className="ml-2" size={16} />
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-lg border border-[#E2E8F0] p-8">
                <h3 className="text-lg font-['Outfit'] font-semibold text-[#0B1B3D] mb-6">Elérhetőségeink</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0B1B3D]/5 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone size={18} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1A1F2C]">Telefon</p>
                      <p className="text-sm text-[#64748B]">+36 30 990 6766</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0B1B3D]/5 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail size={18} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1A1F2C]">Email</p>
                      <p className="text-sm text-[#64748B]">office@agmconsulting.hu</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#0B1B3D]/5 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin size={18} className="text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1A1F2C]">Cím</p>
                      <p className="text-sm text-[#64748B]">1088 Budapest, Bródy Sándor utca 19.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-[#0B1B3D] rounded-lg p-8">
                <h3 className="text-lg font-['Outfit'] font-semibold text-white mb-3">Nyitvatartás</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-[#94A3B8]">
                    <span>Hétfő – Péntek</span>
                    <span className="text-white font-medium">8:00 – 17:00</span>
                  </div>
                  <div className="flex justify-between text-[#94A3B8]">
                    <span>Szombat – Vasárnap</span>
                    <span className="text-white font-medium">Zárva</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
